import Location16 from '@carbon/icons-react/es/location/16';
import { Tooltip } from 'antd';
import get from 'lodash/get';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'components/atoms/button';
import { OrphanTasksInfo } from 'components/molecules/documents/orphan-tasks';
import { useDocuments } from 'components/organisms/document/document-store';
import { SquadProvider } from 'contexts/providers/squad';
import {
  createDocSectionTask,
  selectDocumentActiveTaskId,
  selectDocumentTasksContentMap,
  selectTaskSectionIdNameMap,
  updateActiveTask,
} from 'data/documents';
import { Task } from 'data/tasks';
import { useTranslation } from 'hooks/use-translation';
import { useDocSectionTasks, useDocTasks } from '../../document-tasks-hook';
import { DocTaskProps } from '../types';
import { DocumentTasksBase } from './doc-tasks-tab';
import styles from './doc-tasks-tab.module.css';

interface TaskSection {
  name?: string;
  items: Task[];
}

const ScrollToTheRefTask = ({ task }) => {
  const [docState] = useDocuments();
  const dispatch = useDispatch();
  const scrollToTheRef = useTranslation('document.tasks.scroll_to_the_ref');

  return (
    <Tooltip title={scrollToTheRef}>
      <div className={styles.scroll_to_ref_btn}>
        <Button
          type="link"
          icon={<Location16 />}
          size="small"
          onClick={() => {
            const options = {
              taskId: `rl-task-${task.taskId}`,
            };

            docState.editor?.execute('activeTaskCommand', options);
            dispatch(updateActiveTask(task.taskId));
          }}
        />
      </div>
    </Tooltip>
  );
};

export const DocumentTextTasks: FC<DocTaskProps> = (props) => {
  const [state] = useDocuments();
  const { documentObj } = state;
  const documentSectionIds = documentObj?.sections.reduce((acc, section) => {
    acc.push(section?.documentSectionId);

    return acc;
  }, [] as number[]);

  const { projectId, documentId, docTitle } = props;

  const dispatch = useDispatch();

  const resourceSectionIdNameMap = useSelector(selectTaskSectionIdNameMap);
  const tasksDocMap = useSelector(selectDocumentTasksContentMap);
  const activeTaskId = useSelector(selectDocumentActiveTaskId);

  const docTaskLabel = useTranslation('document.tasks');

  const [showOrphanTasks, setOrphanTasksVisibility] = useState<boolean>(false);

  // `docTasksFromStore` are the ones created directly on DOCUMENT instead of DOCUMENT_SECTION.
  const docTasksFromStore = useDocTasks(projectId, documentId);
  const generalTasks = docTasksFromStore.filter((task) => {
    return task.source?.metaData
      ? !task.source?.metaData.resourceSectionId
      : true;
  });
  const generalTaskSections = [
    {
      name: docTaskLabel,
      items: generalTasks.reduce((acc, task) => {
        if (tasksDocMap[task.taskId] && tasksDocMap[task.taskId].isActive) {
          acc.push(task);
        } else if (!task.source?.metaData?.taskFromDoc) {
          acc.push(task);
        }

        return acc;
      }, [] as Task[]),
    },
    {
      items: generalTasks.reduce((acc, task) => {
        if (tasksDocMap[task.taskId] && !tasksDocMap[task.taskId].isActive) {
          acc.push(task);
        } else if (
          !tasksDocMap[task.taskId] &&
          task.source?.metaData?.taskFromDoc
        ) {
          acc.push(task);
        }

        return acc;
      }, [] as Task[]),
    },
  ];

  // `docSectionTasksFromStore` are the ones created directly on DOCUMENT_SECTION instead of DOCUMENT.
  const docSectionTasksFromStore = useDocSectionTasks(projectId);
  const tasksSectionArray = Object.keys(resourceSectionIdNameMap).reduce(
    (acc, resourceSectionId) => {
      if (
        resourceSectionIdNameMap[resourceSectionId]?.parentDoc === documentId
      ) {
        const taskSectionObj = {
          name: resourceSectionIdNameMap[resourceSectionId].content,
          items: docSectionTasksFromStore.filter((task) => {
            if (task.source?.metaData) {
              return (
                task.source?.metaData.resourceSectionId ===
                  Number(resourceSectionId) &&
                documentSectionIds?.includes(task.source?.sourceId)
              );
            }

            return false;
          }),
        };

        acc.push(taskSectionObj);
      }

      return acc;
    },
    [] as TaskSection[],
  );

  useEffect(() => {
    document
      .querySelector(`.ck [data-taskid="rl-task-${activeTaskId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeTaskId]);

  const renderExtraActions = (task: Task) => {
    return task.source?.metaData?.taskFromDoc ? (
      <ScrollToTheRefTask task={task} />
    ) : null;
  };

  const handleAddNewSectionTask = (
    taskName,
    { resourceSectionId, documentSectionId, isPrivate },
  ) => {
    dispatch(
      createDocSectionTask({
        documentId: documentId as number,
        projectId: projectId as number,
        taskName,
        meta: {
          resourceSectionId,
          documentSectionId: Number(documentSectionId),
          private: isPrivate,
        },
      }),
    );
  };

  return (
    <div className={styles.task__sections}>
      <SquadProvider projectId={projectId}>
        <DocumentTasksBase
          projectId={projectId}
          renderExtraActions={renderExtraActions}
          documentId={documentId}
          tasks={generalTaskSections[0]?.items}
          name={generalTaskSections[0]?.name}
          docTitle={docTitle}
        />
        <OrphanTasksInfo
          className={styles.orphan__wrapper}
          onOrphanTaskVisibilityChange={() => {
            dispatch(updateActiveTask(null));
            setOrphanTasksVisibility(!showOrphanTasks);
          }}
          isOrphanTasksVisible={showOrphanTasks}
          total={generalTaskSections[1]?.items.length}
        />
        {showOrphanTasks ? (
          <DocumentTasksBase
            disableNewRow
            projectId={projectId}
            documentId={documentId}
            tasks={generalTaskSections[1]?.items}
            docTitle={docTitle}
          />
        ) : null}
        {tasksSectionArray.map((taskSection, index) => {
          return (
            <DocumentTasksBase
              // eslint-disable-next-line react/no-array-index-key
              key={`${index}-${taskSection.name}`}
              resourceSectionId={get(taskSection.items, [
                0,
                'source',
                'metaData',
                'resourceSectionId',
              ])}
              documentSectionId={get(taskSection.items, [
                0,
                'source',
                'sourceId',
              ])}
              isPrivate={
                // this may not work when we implement individual private task in task section: @asimkt
                get(taskSection.items, [0, 'private'])
              }
              handleAddNewTask={handleAddNewSectionTask}
              projectId={projectId}
              documentId={documentId}
              tasks={taskSection.items}
              name={taskSection.name}
              docTitle={docTitle}
            />
          );
        })}
      </SquadProvider>
    </div>
  );
};
